import { useState, useCallback, useEffect } from 'react';
import { COIN_SIZE } from '@/domain/constants';
import { CircuitRings } from './CircuitRings';

interface TapCoinProps {
  isActive: boolean;
  canPlay: boolean; // Sessions available (glow + rotate when true)
  onTap: () => void;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  angle: number;
  speed: number;
  size: number;
  opacity: number;
}

export function TapCoin({ isActive, canPlay, onTap }: TapCoinProps) {
  const [isPressed, setIsPressed] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const [particles, setParticles] = useState<Particle[]>([]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isActive) return;
      if (!e.isPrimary) return;
      // For mouse, only accept left-click.
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);

      setIsPressed(true);
      setPulseKey((k) => k + 1);
      onTap();
    },
    [isActive, onTap]
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.isPrimary) return;
    setIsPressed(false);
  }, []);

  const handlePointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.isPrimary) return;
    setIsPressed(false);
  }, []);

  // Particle effect when canPlay but not active (inviting to play)
  useEffect(() => {
    if (!canPlay || isActive) {
      setParticles([]);
      return;
    }

    const spawnParticle = () => {
      const angle = Math.random() * Math.PI * 2;
      const radius = COIN_SIZE / 2;
      const x = Math.cos(angle) * radius * (0.7 + Math.random() * 0.3);
      const y = Math.sin(angle) * radius * (0.7 + Math.random() * 0.3);

      return {
        id: Date.now() + Math.random(),
        x,
        y,
        angle: angle - Math.PI / 2 + (Math.random() - 0.5) * 0.5, // Float upward with variance
        speed: 0.3 + Math.random() * 0.4,
        size: 2 + Math.random() * 2,
        opacity: 0.6 + Math.random() * 0.4,
      };
    };

    // Spawn particles periodically
    const spawnInterval = setInterval(() => {
      setParticles((prev) => {
        const newParticles = [...prev, spawnParticle(), spawnParticle()];
        // Keep max 25 particles
        return newParticles.slice(-25);
      });
    }, 150);

    // Animate particles
    const animateInterval = setInterval(() => {
      setParticles((prev) =>
        prev
          .map((p) => ({
            ...p,
            x: p.x + Math.cos(p.angle) * p.speed,
            y: p.y + Math.sin(p.angle) * p.speed - 0.5, // Float up
            opacity: p.opacity - 0.02,
          }))
          .filter((p) => p.opacity > 0)
      );
    }, 30);

    return () => {
      clearInterval(spawnInterval);
      clearInterval(animateInterval);
    };
  }, [canPlay, isActive]);

  // Circuit rings are larger than the coin
  const ringsSize = COIN_SIZE + 60;

  // Opacity and filter based on state
  const getVisualState = () => {
    if (isActive) {
      return {
        opacity: isPressed ? 0.95 : 0.9,
        filter: isPressed ? 'brightness(1.1)' : 'brightness(1.0)',
      };
    }
    if (canPlay) {
      // No filter - full original image when sessions available
      return { opacity: 1, filter: 'none' };
    }
    // Dimmed when no sessions
    return { opacity: 0.5, filter: 'brightness(0.4) saturate(0.5)' };
  };

  const visualState = getVisualState();

  return (
    <div
      className="no-select relative cursor-pointer"
      style={{
        width: ringsSize,
        height: ringsSize,
        touchAction: isActive ? 'none' : 'manipulation',
        transform: isPressed ? 'scale(0.97)' : 'scale(1)',
        transformOrigin: 'center center',
        transition: 'transform 100ms ease-out',
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerLeave={handlePointerCancel}
    >
      {/* Circuit rings behind everything */}
      <CircuitRings size={ringsSize} isActive={isActive} canPlay={canPlay} />

      {/* Coin container centered within rings */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ width: COIN_SIZE, height: COIN_SIZE }}
      >
        {/* Pulse ring on tap */}
        {isActive && (
          <div
            key={pulseKey}
            className="absolute inset-0 rounded-full"
            style={{
              border: '2px solid rgba(0, 191, 255, 0.6)',
              animation: 'tap-pulse 0.3s ease-out forwards',
            }}
          />
        )}

        {/* Logo image */}
        <div
          className="absolute inset-0 overflow-hidden rounded-full transition-all duration-300"
          style={{
            opacity: visualState.opacity,
            filter: visualState.filter,
          }}
        >
          <img
            src="/xcoin.png"
            alt="X Logo"
            className="h-full w-full object-contain"
            draggable={false}
          />
        </div>

        {/* Evaporation particles when ready to play */}
        {particles.map((p) => (
          <div
            key={p.id}
            className="pointer-events-none absolute rounded-full"
            style={{
              left: `calc(50% + ${p.x}px)`,
              top: `calc(50% + ${p.y}px)`,
              width: p.size,
              height: p.size,
              backgroundColor: `rgba(0, 220, 255, ${p.opacity})`,
              boxShadow: `0 0 ${p.size * 2}px rgba(0, 220, 255, ${p.opacity * 0.5})`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        ))}
      </div>
    </div>
  );
}
